import { createDefaultDetailPageTitleFn, DetailPageLayout } from '@app/DetailPageLayout';
import { DetailPageRightMenu } from '@common/detailPageRightMenu/DetailPageRightMenu';
import { EDetailPageRightMenuGeneralInfoType } from '@common/detailPageRightMenu/detailPageRightMenuGeneralInfo.models';
import { Tab, Tabs } from '@core/Tabs/Tab';
import { Badge, ComponentType, Form, Grid, IBreadcrumbItem, IconName, useForm } from '@gcl/react-core';
import { routes } from '@routes/routes.models';
import * as React from 'react';
import { getAccessMode } from '@common/detailPage.hooks';
import { getMasterScheduleDetail } from '@masterSchedule/masterSchedule.hooks';
import { useFetchDesignBrief, useGetDesignBriefRevisions } from '@designBrief/designBrief.queries';
import { useNavigate } from '@routes/routes.hooks';
import { useProjectId } from '@config/config.hooks';
import { EProjectFileType } from '@file/projectFile/projectFile.constants';
import { useFetchAttachmentDesignBrief } from '@file/projectFile/projectFile.queries';
import { DiscussionList } from '@discussion/DiscussionList';
import { EProjectDiscussionFormType } from '@discussion/projectDiscussion/projectDiscussion.constants';
import { Attachments } from '@file/Attachments';
import { EProjectDiscussionFileType } from '@discussion/projectDiscussion/projectDiscussionFile/projectDiscussionFile.constants';
import { IProjectFileAttachmentsDesignBriefsRequest } from '@file/projectFile/projectFile.models';
import { useSearchParams } from 'react-router-dom';
import { EDesignBriefDetailSearchParamKey } from '@designBrief/designBrief.models';
import { useAllowT2Comment, useAllowT2Response, useT2Visibility } from '@designBrief/designBrief.hooks';
import { useSelectRevisionListModal } from '@common/SelectRevisionModal.hooks';
import { ESelectRevisionModalType } from '@common/SelectRevisionModal.constants';
import usePrompt from '@common/prompt.hooks';
import { IMasterScheduleDetail } from '@masterSchedule/detail/masterScheduleDetail.models';
import { useFetchInfoAttachedOptions } from '@designBrief/infoAttached/infoAttached.queries';
import { IDesignBriefInfoAttached } from '@designBrief/infoAttached/infoAttached.models';
import { EDesignBriefStatus } from '@designBrief/designBrief.constants';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { isRestrictedAccessFromApiResponse } from '@common/api.services';
import { ECompanyTypeIdConst } from '@company/companyType/companyType.constants';
import { IProjectCompanyOption } from '@company/projectCompany/projectCompany.models';
import { DesignBriefDetailTab } from './DesignBriefDetailTab';
import { EDesignBriefFormKeys, IDesignBrief, IDesignBriefFormState } from './designBriefDetailPage.models';

interface IDesignBriefDetailPageProps {}

export const DesignBriefDetailPage: React.FunctionComponent<IDesignBriefDetailPageProps> = ({}) => {
  const { id, mode } = getAccessMode();

  const { navigate, projectNo } = useNavigate();
  const projectId = useProjectId();

  const useFormMethods = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
  });

  const {
    formState: { isDirty },
  } = useFormMethods;

  const [searchParams] = useSearchParams();
  const searchParamMasterScheduleId =
    searchParams.get(EDesignBriefDetailSearchParamKey.masterScheduleId) !== null
      ? parseInt(searchParams.get(EDesignBriefDetailSearchParamKey.masterScheduleId) ?? '0', 10)
      : undefined;

  const {
    data: designBriefDetail,
    isLoading: isLoadingDesignBrief,
    isRefetching: isRefetchingDesignBrief,
    error: errorGetDesignBrief,
  } = useFetchDesignBrief({ projectId }, id, !isDirty);

  const {
    data: dataProjectDesignBriefRevisions,
    isLoading: isLoadingProjectDesignBriefRevisions,
    isRefetching: isRefetchingProjectDesignBriefRevisions,
    error: errorProjectDesignBriefRevisions,
  } = useGetDesignBriefRevisions({ projectId }, designBriefDetail?.id, !isDirty);

  const {
    data: infoAttachedOptions,
    isLoading: isLoadingInfoAttachedOptions,
    isRefetching: isRefetchingInfoAttachedOptions,
    error: errorInfoAttachedOptions,
  } = useFetchInfoAttachedOptions(!isDirty);

  const masterScheduleId = mode === 'Create' ? searchParamMasterScheduleId : designBriefDetail?.projectMasterScheduleId;

  const {
    masterSchedule,
    designPackageData,
    responsiblePersonsAndTeamsData,
    isLoading: isLoadingMasterSchedule,
    isRefetching: isRefetchingMasterSchedule,
    error: errorMasterSchedule,
  } = getMasterScheduleDetail(projectId, masterScheduleId, !isDirty);

  const isRefetching =
    isRefetchingDesignBrief ||
    isRefetchingMasterSchedule ||
    isRefetchingProjectDesignBriefRevisions ||
    isRefetchingInfoAttachedOptions;

  const { isReadOnlyDetailTab, modeSupplementaryInfo, isReadOnlyCommentTab } = useT2Visibility(
    masterSchedule,
    designBriefDetail,
    mode,
  );

  const { canCreateComment, canViewDraftComment } = useAllowT2Comment(masterSchedule, designBriefDetail);

  const canReplyComment = useAllowT2Response(masterSchedule, designBriefDetail);

  const breadcrumbItems: IBreadcrumbItem[] = [
    {
      text: `${projectNo} - Home`,
      prefixIconName: 'House',
      onClick: () => {
        navigate(routes.t1.path);
      },
    },
    {
      text: 'Search T2',
      prefixIconName: 'Search' as IconName,
      onClick: () => {
        navigate(routes.t2.path);
      },
    },
    {
      text: `${mode} T2`,
      prefixIconName: 'JournalText' as IconName,
      active: true,
    },
  ];

  const {
    openPortal,
    renderModal: renderRevisionListModal,
    badge: revisionBadge,
  } = useSelectRevisionListModal({
    type: ESelectRevisionModalType.T2,
    revisions: dataProjectDesignBriefRevisions,
  });

  const getDefaultFormValues = (
    isRefetchingLocal: boolean,

    designBriefLocal?: IDesignBrief,
    masterScheduleLocal?: IMasterScheduleDetail,
    infoAttachedOptionsLocal?: IDesignBriefInfoAttached[],
  ) => {
    if (isRefetchingLocal && useFormMethods.formState.defaultValues) return useFormMethods.formState.defaultValues;

    let designer: IProjectCompanyOption = {
      id: -1,
      name: '',
      code: '',
      projectId,
      maxRiskCategory: '',
      companyTypeId: -1,
      companyTypeName: '',
      organizationTypeId: -1,
      organizationTypeCode: '',
      rowVersion: '',
    };
    if (designBriefDetail?.carryOutDesignerCompanyId && designBriefDetail?.carryOutDesignerCompany)
      designer = {
        ...designer,
        id: designBriefDetail?.carryOutDesignerCompanyId,
        name: designBriefDetail?.carryOutDesignerCompany,
      };
    if (
      responsiblePersonsAndTeamsData.responsibleDesigner?.companyTypeId &&
      responsiblePersonsAndTeamsData.responsibleDesigner?.companyTypeId !== ECompanyTypeIdConst.PLACEHOLDER
    ) {
      designer = {
        ...designer,
        id: responsiblePersonsAndTeamsData.responsibleDesigner?.id,
        name: responsiblePersonsAndTeamsData.responsibleDesigner?.name,
      };
    }

    const formState: IDesignBriefFormState = {
      [EDesignBriefFormKeys.RISK_CATEGORY]: masterScheduleLocal?.riskCategory?.category ?? null,
      [EDesignBriefFormKeys.DESIGNER]: designer,
      [EDesignBriefFormKeys.DESC_OF_WORKS_TO_BE_DESIGNED]: designBriefLocal?.briefDescription ?? null,
      [EDesignBriefFormKeys.INFO_ATTACHED]:
        designBriefLocal?.infoAttached ??
        (infoAttachedOptionsLocal ?? []).map((option) => {
          return {
            designBriefInfoAttached: option,
            designBriefInfoAttachedId: option.id,
            itemSelected: false,
            projectId,
            rowVersion: undefined,
            remark: null,
            name: option.itemName,
            projectDesignBriefId: undefined,
          };
        }),
      [EDesignBriefFormKeys.FILES]:
        designBriefLocal?.attachments?.map((attachment) => {
          return { ...attachment, isTemp: false };
        }) ?? [],
      [EDesignBriefFormKeys.SUPPLEMENTARY_FILES]: [],
    };
    return formState;
  };

  const isRestricted = React.useMemo(() => {
    return isRestrictedAccessFromApiResponse([errorGetDesignBrief]);
  }, [JSON.stringify(errorGetDesignBrief)]);

  return (
    <>
      <ApiErrorDisplay
        errors={[
          { error: errorGetDesignBrief, genericError: 'Get design brief failed' },
          { error: errorInfoAttachedOptions, genericError: 'Get info attached options failed' },
          { error: errorProjectDesignBriefRevisions, genericError: 'Get revisions failed' },
          { error: errorMasterSchedule, genericError: 'Get master schedule failed' },
        ]}
      />
      <DetailPageLayout
        errorGetDetail={errorGetDesignBrief}
        isRestricted={isRestricted}
        data={
          mode === 'Create'
            ? masterSchedule && infoAttachedOptions
            : masterSchedule && infoAttachedOptions && designBriefDetail
        }
        isRefetching={isRefetching}
        isLoading={
          isLoadingDesignBrief ||
          isLoadingMasterSchedule ||
          isLoadingProjectDesignBriefRevisions ||
          isLoadingInfoAttachedOptions
        }
        rightMenu={(setIsDrawerOpen) => (
          <DetailPageRightMenu
            setIsDrawerOpen={setIsDrawerOpen}
            type={EDetailPageRightMenuGeneralInfoType.T2}
            masterSchedule={
              masterSchedule
                ? ({
                    ...masterSchedule,
                    id: masterSchedule?.id,
                    refNo: designPackageData?.refNo || '',
                    designPackage: designPackageData?.designPackage || '',
                    responsibleDesigner: responsiblePersonsAndTeamsData?.responsibleDesigner,
                    riskCategory: designPackageData?.riskCategory,
                  } as IMasterScheduleDetail)
                : undefined
            }
          />
        )}
        title={createDefaultDetailPageTitleFn(
          <>
            {`T2 Design Brief${
              mode !== 'Create' && designBriefDetail?.designBriefNo ? ` - ${designBriefDetail?.designBriefNo}` : ''
            }`}
            {revisionBadge}
            {(masterSchedule?.isCancelled || designBriefDetail?.status === EDesignBriefStatus.CANCELLED) && (
              <Badge pill type={ComponentType.Secondary} className="gcl-ml-sm gcl-fb-md gcl-fc-greyscale-100 ">
                {masterSchedule?.isCancelled ? 'T1 Cancelled' : 'Cancelled'}
              </Badge>
            )}
          </>,
        )}
        breadcrumb={breadcrumbItems}
        render={(DetailPageRightMenuOpener) => {
          return (
            <>
              <Grid container rowSpacing={1}>
                <Grid item xs={12}>
                  <Tabs>
                    <Tab headerText="Details">
                      <Form
                        defaultValues={{
                          callback: () => {
                            const state = getDefaultFormValues(
                              isRefetching,

                              designBriefDetail,
                              masterSchedule,
                              infoAttachedOptions,
                            );
                            return state;
                          },
                          deps: [masterSchedule, designBriefDetail, infoAttachedOptions, isRefetching],
                        }}
                        enableUnsavedChangesPrompt
                        unsavedChangesPromptProps={{
                          usePrompt,
                        }}
                        useFormMethods={useFormMethods}
                        render={(formFieldMethods) => {
                          return (
                            <DesignBriefDetailTab
                              useFormMethods={useFormMethods}
                              isReadOnly={isReadOnlyDetailTab}
                              designBriefId={id}
                              masterSchedule={masterSchedule}
                              masterScheduleId={masterScheduleId}
                              mode={mode}
                              projectId={projectId}
                              infoAttachedOptions={infoAttachedOptions}
                            />
                          );
                        }}
                      />
                    </Tab>
                    <Tab headerText="Supplementary Info">
                      <Attachments<IProjectFileAttachmentsDesignBriefsRequest>
                        id={`supplementary-info-${EProjectFileType.T2_SUPPLEMENTARY}`}
                        mode={modeSupplementaryInfo}
                        hasUploadRight={modeSupplementaryInfo !== 'View'}
                        fileType={EProjectFileType.T2_SUPPLEMENTARY}
                        uploadFileRequestParam={{ designBriefId: id }}
                        useFetchFiles={useFetchAttachmentDesignBrief}
                        useFetchFilesProps={{
                          designBriefId: id ?? 0,
                          projectId,
                          fileType: EProjectFileType.T2_SUPPLEMENTARY,
                        }}
                        formKeyToUpdateInCreateMode={EDesignBriefFormKeys.SUPPLEMENTARY_FILES}
                      />
                    </Tab>
                    {!!id && (
                      <Tab headerText="Comment">
                        <DiscussionList
                          isReadOnly={isReadOnlyCommentTab}
                          formType={EProjectDiscussionFormType.DESIGN_BRIEF}
                          projectDesignBriefId={id ?? 0}
                          fileFormType={EProjectFileType.DESIGN_BRIEF_DISCUSSION_ATTACHMENT}
                          discussionFileType={EProjectDiscussionFileType.DESIGN_BRIEF}
                          canComment={canCreateComment}
                          canResponse={canReplyComment}
                          canViewDraftComment={canViewDraftComment}
                        />
                      </Tab>
                    )}
                  </Tabs>
                </Grid>
              </Grid>
              {renderRevisionListModal()}
            </>
          );
        }}
      />
    </>
  );
};
